import { useEffect, useState, useCallback } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Cookies from 'js-cookie'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Loader2,
  ScrollText,
  AlertCircle,
  User,
  Cpu,
  Timer,
  RefreshCcw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { BASE_URL } from '@/constant'

dayjs.extend(utc)

interface CronLog {
  id: number
  sync_time: string
  status: string
  source: string
  error_message: string | null
  completed_at: string | null
}

interface CronLogsResponse {
  message: string
  data: CronLog[]
}

const CronLogs = () => {
  const [logs, setLogs] = useState<CronLog[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchLogs = useCallback(async (isManual = false) => {
    try {
      if (isManual) setRefreshing(true)
      else setLoading(true)

      const token = Cookies.get('token')
      const response = await axios.get<CronLogsResponse>(
        `${BASE_URL}/configurations/schedule/logs`,
        {
          params: { _t: Date.now() },
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Cache-Control': 'no-cache',
          },
        }
      )
      setLogs(response.data.data)
      setError(null)
    } catch {
      setError('Failed to load activity logs')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const getDuration = (start: string, end: string | null) => {
    if (!end) return '—'
    const startTime = dayjs.utc(start)
    const endTime = dayjs.utc(end)
    const diffMs = endTime.diff(startTime)
    if (diffMs < 0) return '—'
    const totalSeconds = Math.floor(diffMs / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    if (minutes > 0) return `${minutes}m ${seconds}s`
    return `${seconds}s`
  }

  const getStatusBadge = (status: string) => {
    const isSuccess = status.toUpperCase() === 'SUCCESS'
    return (
      <div className='flex items-center gap-2'>
        <span
          className={cn(
            'w-1.5 h-1.5 rounded-full',
            isSuccess ? 'bg-primary' : 'bg-destructive'
          )}
        />
        <span className='text-xs font-bold uppercase tracking-tight text-foreground'>
          {status}
        </span>
      </div>
    )
  }

  const getSourceBadge = (source: string) => {
    const isManual = source.toLowerCase() === 'manual'
    return (
      <div className='flex items-center gap-2 text-muted-foreground'>
        {isManual ? (
          <User className='w-3.5 h-3.5' />
        ) : (
          <Cpu className='w-3.5 h-3.5' />
        )}
        <span className='text-xs font-semibold uppercase tracking-tight text-foreground'>
          {source}
        </span>
      </div>
    )
  }

  if (loading) {
    return (
      <div className='flex flex-col items-center justify-center py-20 bg-card rounded-2xl border border-border border-dashed'>
        <Loader2 className='w-8 h-8 animate-spin text-muted-foreground/40' />
        <div className='text-center mt-4'>
          <p className='text-sm font-bold text-foreground'>Fetching logs</p>
          <p className='text-[11px] text-muted-foreground'>
            Updating execution history...
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center py-16 bg-destructive/[0.02] rounded-2xl border border-destructive/10'>
        <AlertCircle className='w-8 h-8 text-destructive/50 mb-3' />
        <p className='text-sm font-bold text-foreground'>
          Something went wrong
        </p>
        <p className='text-[11px] text-muted-foreground mt-1'>{error}</p>
        <Button
          variant='outline'
          size='sm'
          onClick={() => fetchLogs()}
          className='mt-6 rounded-full border-destructive/20 text-destructive hover:bg-destructive/10'
        >
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className='h-full flex flex-col space-y-6 overflow-hidden'>
      <div className='flex-1 flex flex-col rounded-lg border border-border bg-card overflow-hidden'>
        {/* Header Section */}
        <div className='shrink-0 flex items-center justify-between px-8 py-6 border-b border-border bg-muted/30'>
          <div className='flex items-center gap-4'>
            <div className='w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-muted-foreground'>
              <ScrollText className='w-5 h-5' />
            </div>
            <div>
              <h3 className='text-sm font-bold text-foreground'>
                Sync Activity Logs
              </h3>
              <p className='text-xs text-muted-foreground mt-0.5'>
                History of automated system executions
              </p>
            </div>
          </div>

          <Button
            variant='outline'
            size='sm'
            onClick={() => fetchLogs(true)}
            disabled={refreshing}
            className='rounded-full h-8 px-4 border-border text-[11px] font-bold'
          >
            <RefreshCcw
              className={cn(
                'w-3 h-3 mr-2 text-primary',
                refreshing && 'animate-spin'
              )}
            />
            {refreshing ? 'Syncing...' : 'Refresh Logs'}
          </Button>
        </div>

        {/* Table Content */}
        <div className='flex-1 overflow-auto'>
          <Table className='border-none'>
            <TableHeader className='sticky top-0 z-20 bg-background'>
              <TableRow className='hover:bg-transparent border-b border-border/50'>
                <TableHead className='px-8 h-12 text-[10px] font-bold uppercase tracking-[0.1em]'>
                  Execution Time
                </TableHead>
                <TableHead className='h-12 text-[10px] font-bold uppercase tracking-[0.1em]'>
                  Source
                </TableHead>
                <TableHead className='h-12 text-[10px] font-bold uppercase tracking-[0.1em]'>
                  Duration
                </TableHead>
                <TableHead className='px-8 h-12 text-[10px] font-bold uppercase tracking-[0.1em] text-right'>
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className='h-64 text-center'>
                    <div className='flex flex-col items-center justify-center gap-4 opacity-50'>
                      <div className='w-16 h-16 rounded-2xl bg-muted flex items-center justify-center'>
                        <ScrollText className='w-8 h-8 text-muted-foreground' />
                      </div>
                      <div className='space-y-1'>
                        <p className='text-sm font-bold text-foreground'>
                          No activity logs yet
                        </p>
                        <p className='text-[11px] text-muted-foreground'>
                          Sync history will appear here once executed.
                        </p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map(log => (
                  <TableRow
                    key={log.id}
                    className='group hover:bg-muted/30 border-b border-border/50 transition-colors'
                  >
                    <TableCell className='px-8 py-5'>
                      <div className='flex flex-col'>
                        <span className='text-sm font-bold text-foreground leading-none'>
                          {dayjs
                            .utc(log.sync_time)
                            .local()
                            .format('MMM D, YYYY')}
                        </span>
                        <span className='text-[11px] font-medium text-muted-foreground mt-1'>
                          {dayjs.utc(log.sync_time).local().format('h:mm:ss A')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className='py-5'>
                      {getSourceBadge(log.source)}
                    </TableCell>
                    <TableCell className='py-5'>
                      <div className='flex items-center gap-2'>
                        <Timer className='w-3.5 h-3.5 text-muted-foreground' />
                        <span className='text-sm font-bold text-foreground'>
                          {getDuration(log.sync_time, log.completed_at)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className='px-8 py-5 text-right'>
                      <div className='flex flex-col items-end gap-1'>
                        {getStatusBadge(log.status)}
                        {log.error_message && (
                          <div
                            className='flex items-center gap-1.5 text-[10px] font-bold text-destructive max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap cursor-help bg-destructive/5 px-2 py-0.5 rounded border border-destructive/10'
                            title={log.error_message}
                          >
                            <AlertCircle className='w-2.5 h-2.5' />
                            {log.error_message}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}

export default CronLogs
